/**
 * Summary-field registry — the configurable "top bar" (plan P2,
 * docs/REPORT_TEMPLATES_AND_PREVIEW_PLAN.md).
 *
 * One catalogue of the values a report's quick summary can show: the
 * wizard rail's Nøkkeltal, the lønn PDF "Oppsummering" box, the shift PDF
 * summary box, the lønn preview totals and the dashboard month-to-date
 * card all read THIS list, so they can never disagree again.
 *
 * Every key's label says what it includes — there is no bare "Kontant"
 * or "Brutto": bomtur and tips are the user's choice (kontantInklBomtur /
 * kontantEksBomtur / bomtur, bruttoInklTips / bruttoEksTips / tips are
 * all separate keys). The user picks and orders keys per template or as
 * a personal default; `DEFAULT_SUMMARY_FIELDS` is only the starting
 * point and reproduces today's PDF boxes exactly.
 *
 * The compute functions only SELECT and FORMAT figures that the existing
 * math produced (calculateSalarySummary / computeFleetTotals /
 * calculateShiftSummary) — nothing is recomputed here. Pure; function
 * declarations per the TDZ rule in CLAUDE.md.
 */

import { formatNorwegianNumber } from '../lonn-utils';

export type SummaryReportType = 'shift' | 'lonn';

export type SummaryFieldKey =
  // Lønn
  | 'lonnsgrunnlag'
  | 'bruttoEksTips'
  | 'tips'
  | 'bruttoInklTips'
  | 'kontantInklBomtur'
  | 'bomtur'
  | 'kontantEksBomtur'
  | 'omsetning'
  | 'lonnsprosent'
  | 'skift'
  | 'sjaforar'
  // Shift
  | 'skiftnrRange'
  | 'kreditt'
  | 'rader'
  | 'ekskluderteRader';

/**
 * Lønn figures as the existing math produces them (FleetTotals-shaped;
 * a single driver's SalarySummary maps onto it via
 * `lonnSummaryInputFromSalary`). `brutto` INCLUDES tips, exactly like
 * calculateSalarySummary; `tipsInBrutto` lets a caller whose brutto
 * covers only some drivers (the dashboard's registered-only salary)
 * say how much tips that brutto contains — defaults to `tipsSum`.
 */
export interface LonnSummaryInput {
  rowCount: number;
  subtotalSum: number;
  kontantSum: number;
  bomturSum: number;
  kontantVal: number;
  tipsSum: number;
  brutto: number;
  lonnsgrunnlag: number;
  tipsInBrutto?: number;
  driverPercent?: number;
  driverCount?: number;
}

/** Shift figures as calculateShiftSummary / buildReportData produce them. */
export interface ShiftSummaryInput {
  shiftRange: string;
  totalKreditt: number;
  totalKontant: number;
  totalBomtur: number;
  totalRows: number;
  excludedRows?: number;
}

export type SummaryValue =
  | { kind: 'currency'; amount: number }
  | { kind: 'count'; count: number }
  | { kind: 'percent'; percent: number }
  | { kind: 'text'; text: string };

export interface SummaryFieldDef {
  key: SummaryFieldKey;
  /** i18n key for on-screen labels (nb + en). */
  labelKey: string;
  /** Printed label — the PDFs are Norwegian-only today. */
  pdfLabel: string;
  reportTypes: readonly SummaryReportType[];
  /** Highlighted in the rail / card (revenue-ish headline figure). */
  accent?: boolean;
  lonn?: (input: LonnSummaryInput) => SummaryValue | null;
  shift?: (input: ShiftSummaryInput) => SummaryValue | null;
}

function kr(amount: number): SummaryValue {
  return { kind: 'currency', amount };
}
function count(n: number): SummaryValue {
  return { kind: 'count', count: n };
}

export const SUMMARY_FIELDS: readonly SummaryFieldDef[] = [
  {
    key: 'lonnsgrunnlag',
    labelKey: 'summaryFields.keys.lonnsgrunnlag',
    pdfLabel: 'Lønnsgrunnlag',
    reportTypes: ['lonn'],
    lonn: (i) => kr(i.lonnsgrunnlag),
  },
  {
    key: 'bruttoEksTips',
    labelKey: 'summaryFields.keys.bruttoEksTips',
    pdfLabel: 'Brutto lønn (eks. tips)',
    reportTypes: ['lonn'],
    lonn: (i) => kr(i.brutto - (i.tipsInBrutto ?? i.tipsSum)),
  },
  {
    key: 'tips',
    labelKey: 'summaryFields.keys.tips',
    pdfLabel: 'Tips',
    reportTypes: ['lonn'],
    lonn: (i) => kr(i.tipsSum),
  },
  {
    key: 'bruttoInklTips',
    labelKey: 'summaryFields.keys.bruttoInklTips',
    pdfLabel: 'Brutto lønn (inkl. tips)',
    reportTypes: ['lonn'],
    accent: true,
    lonn: (i) => kr(i.brutto),
  },
  {
    key: 'kontantInklBomtur',
    labelKey: 'summaryFields.keys.kontantInklBomtur',
    pdfLabel: 'Kontant (inkl. bomtur)',
    reportTypes: ['lonn', 'shift'],
    lonn: (i) => kr(i.kontantSum),
    shift: (i) => kr(i.totalKontant),
  },
  {
    key: 'bomtur',
    labelKey: 'summaryFields.keys.bomtur',
    pdfLabel: 'Bomtur',
    reportTypes: ['lonn', 'shift'],
    lonn: (i) => kr(i.bomturSum),
    shift: (i) => kr(i.totalBomtur),
  },
  {
    key: 'kontantEksBomtur',
    labelKey: 'summaryFields.keys.kontantEksBomtur',
    pdfLabel: 'Kontant (eks. bomtur)',
    reportTypes: ['lonn', 'shift'],
    lonn: (i) => kr(i.kontantVal),
    shift: (i) => kr(i.totalKontant - i.totalBomtur),
  },
  {
    key: 'omsetning',
    labelKey: 'summaryFields.keys.omsetning',
    pdfLabel: 'Omsetning',
    reportTypes: ['lonn'],
    accent: true,
    lonn: (i) => kr(i.subtotalSum),
  },
  {
    key: 'lonnsprosent',
    labelKey: 'summaryFields.keys.lonnsprosent',
    pdfLabel: 'Lønnsprosent',
    reportTypes: ['lonn'],
    lonn: (i) => (i.driverPercent == null ? null : { kind: 'percent', percent: i.driverPercent }),
  },
  {
    key: 'skift',
    labelKey: 'summaryFields.keys.skift',
    pdfLabel: 'Skift',
    reportTypes: ['lonn'],
    lonn: (i) => count(i.rowCount),
  },
  {
    key: 'sjaforar',
    labelKey: 'summaryFields.keys.sjaforar',
    pdfLabel: 'Sjåførar',
    reportTypes: ['lonn'],
    lonn: (i) => (i.driverCount == null ? null : count(i.driverCount)),
  },
  {
    key: 'skiftnrRange',
    labelKey: 'summaryFields.keys.skiftnrRange',
    pdfLabel: 'SKIFTNR',
    reportTypes: ['shift'],
    shift: (i) => ({ kind: 'text', text: i.shiftRange }),
  },
  {
    key: 'kreditt',
    labelKey: 'summaryFields.keys.kreditt',
    pdfLabel: 'KREDITT',
    reportTypes: ['shift'],
    accent: true,
    shift: (i) => kr(i.totalKreditt),
  },
  {
    key: 'rader',
    labelKey: 'summaryFields.keys.rader',
    pdfLabel: 'RADER',
    reportTypes: ['shift'],
    shift: (i) => count(i.totalRows),
  },
  {
    key: 'ekskluderteRader',
    labelKey: 'summaryFields.keys.ekskluderteRader',
    pdfLabel: 'EKSKLUDERTE RADER',
    reportTypes: ['shift'],
    shift: (i) => (i.excludedRows == null ? null : count(i.excludedRows)),
  },
];

/**
 * Built-in starting point — exactly what the PDFs printed before the
 * registry existed, so a user who never touches the setting sees the
 * same numbers with explicit labels.
 */
export const DEFAULT_SUMMARY_FIELDS: Readonly<Record<SummaryReportType, readonly SummaryFieldKey[]>> = {
  lonn: ['kontantEksBomtur', 'lonnsgrunnlag', 'bruttoEksTips', 'tips', 'lonnsprosent'],
  shift: ['skiftnrRange', 'kreditt', 'kontantEksBomtur'],
};

const BY_KEY: ReadonlyMap<string, SummaryFieldDef> = new Map(SUMMARY_FIELDS.map((d) => [d.key, d]));

export function isSummaryFieldKey(value: unknown): value is SummaryFieldKey {
  return typeof value === 'string' && BY_KEY.has(value);
}

export function summaryFieldDef(key: SummaryFieldKey): SummaryFieldDef {
  return BY_KEY.get(key)!;
}

/** The fields a report type can show, in catalogue order. */
export function summaryFieldsFor(reportType: SummaryReportType): SummaryFieldDef[] {
  return SUMMARY_FIELDS.filter((d) => d.reportTypes.includes(reportType));
}

/**
 * Keep only known keys valid for the report type, deduplicated, in the
 * given order. Stored configs may carry keys from a newer app version or
 * from the other report type — they are dropped, never crashed on.
 */
export function sanitizeSummaryFields(
  keys: readonly unknown[] | undefined | null,
  reportType: SummaryReportType,
): SummaryFieldKey[] {
  if (!keys) return [];
  const out: SummaryFieldKey[] = [];
  for (const k of keys) {
    if (!isSummaryFieldKey(k) || out.includes(k)) continue;
    if (!BY_KEY.get(k)!.reportTypes.includes(reportType)) continue;
    out.push(k);
  }
  return out;
}

export interface ResolveSummaryFieldsInput {
  /** The active template's own choice, if any. */
  templateFields?: readonly unknown[] | null;
  /** The user's personal default for the report type, if any. */
  userDefaults?: readonly unknown[] | null;
  reportType: SummaryReportType;
}

/** Template → user default → built-in. An empty/invalid level is skipped. */
export function resolveSummaryFields({
  templateFields,
  userDefaults,
  reportType,
}: ResolveSummaryFieldsInput): SummaryFieldKey[] {
  const fromTemplate = sanitizeSummaryFields(templateFields, reportType);
  if (fromTemplate.length > 0) return fromTemplate;
  const fromUser = sanitizeSummaryFields(userDefaults, reportType);
  if (fromUser.length > 0) return fromUser;
  return [...DEFAULT_SUMMARY_FIELDS[reportType]];
}

export interface SummaryItem {
  key: SummaryFieldKey;
  def: SummaryFieldDef;
  value: SummaryValue;
}

/** Compute the chosen lønn fields; a field with no value here is skipped. */
export function computeLonnSummaryItems(
  keys: readonly SummaryFieldKey[],
  input: LonnSummaryInput,
): SummaryItem[] {
  const items: SummaryItem[] = [];
  for (const key of keys) {
    const def = BY_KEY.get(key);
    if (!def?.lonn) continue;
    const value = def.lonn(input);
    if (value) items.push({ key, def, value });
  }
  return items;
}

/** Compute the chosen shift fields; a field with no value here is skipped. */
export function computeShiftSummaryItems(
  keys: readonly SummaryFieldKey[],
  input: ShiftSummaryInput,
): SummaryItem[] {
  const items: SummaryItem[] = [];
  for (const key of keys) {
    const def = BY_KEY.get(key);
    if (!def?.shift) continue;
    const value = def.shift(input);
    if (value) items.push({ key, def, value });
  }
  return items;
}

/**
 * A single driver's SalarySummary as registry input. Lønnsgrunnlag uses
 * the same `subtotalSum > 0` guard calculateSalarySummary applies to
 * brutto (identical to computeFleetTotals for one driver).
 */
export function lonnSummaryInputFromSalary(
  summary: {
    subtotalSum: number;
    kontantSum: number;
    bomturSum: number;
    kontantVal: number;
    tipsSum: number;
    brutto: number;
    driverPercent: number;
  },
  rowCount: number,
): LonnSummaryInput {
  return {
    rowCount,
    subtotalSum: summary.subtotalSum,
    kontantSum: summary.kontantSum,
    bomturSum: summary.bomturSum,
    kontantVal: summary.kontantVal,
    tipsSum: summary.tipsSum,
    brutto: summary.brutto,
    lonnsgrunnlag: summary.subtotalSum > 0 ? summary.subtotalSum / 1.12 : 0,
    driverPercent: summary.driverPercent,
  };
}

/**
 * Display text. `precise` keeps øre (PDF boxes); otherwise whole kroner
 * (rail / dashboard tiles). Norwegian formatting throughout.
 */
export function formatSummaryValue(value: SummaryValue, precise = false): string {
  switch (value.kind) {
    case 'currency':
      return `${formatNorwegianNumber(precise ? value.amount : Math.round(value.amount))} kr`;
    case 'count':
      return String(value.count);
    case 'percent':
      return `${value.percent.toFixed(2).replace('.', ',')} %`;
    case 'text':
      return value.text;
  }
}
